class FurnitureGallerySlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track || this.controller) return;
    this.cards = [...this.querySelectorAll('[data-gallery-card]')];
    this.previous = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.pause = this.querySelector('[data-gallery-pause]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.controls = this.querySelector('[data-gallery-controls]');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileMedia = matchMedia('(max-width: 749px)');
    this.activeCardIndex = 0;
    this.manualPause = false;
    this.dragging = this.touching = false;
    this.inView = false;
    this.previous?.addEventListener('click', () => this.move(-1), options);
    this.next?.addEventListener('click', () => this.move(1), options);
    this.pause?.addEventListener('click', () => {
      this.manualPause = !this.manualPause;
      this.pause.textContent = this.manualPause ? 'Play' : 'Pause';
      this.pause.setAttribute('aria-label', this.manualPause ? 'Play gallery autoplay' : 'Pause gallery autoplay');
      this.pause.setAttribute('aria-pressed', String(this.manualPause));
      this.scheduleAutoplay();
    }, options);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.normalizeLoop(), 180);
    }, { ...options, passive: true });
    this.addEventListener('focusin', () => this.scheduleAutoplay(), options);
    this.addEventListener('focusout', () => queueMicrotask(() => this.scheduleAutoplay()), options);
    this.track.addEventListener('pointerdown', () => { this.dragging = true; this.stopAutoplay(); }, options);
    const endDrag = () => { this.dragging = false; this.scheduleAutoplay(); };
    window.addEventListener('pointerup', endDrag, options);
    window.addEventListener('pointercancel', endDrag, options);
    // Native horizontal scrolling cancels pointer events as soon as the browser
    // takes over. Keep motion paused until the finger actually leaves the track.
    this.track.addEventListener('touchstart', () => { this.touching = true; this.stopAutoplay(); }, { ...options, passive: true });
    const endTouch = (event) => { this.touching = event.touches.length > 0; this.scheduleAutoplay(); };
    this.track.addEventListener('touchend', endTouch, { ...options, passive: true });
    this.track.addEventListener('touchcancel', endTouch, { ...options, passive: true });
    document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);
    this.reducedMotion.addEventListener('change', () => { this.update(); this.scheduleAutoplay(); }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const card = this.cards.find((item) => item.dataset.blockId === event.detail?.blockId);
      if (!card) return;
      this.manualPause = true;
      if (this.pause) {
        this.pause.textContent = 'Play';
        this.pause.setAttribute('aria-label', 'Play gallery autoplay');
        this.pause.setAttribute('aria-pressed', 'true');
      }
      this.stopAutoplay();
      this.update();
      this.scrollToPosition(Math.min(Math.abs(card.offsetLeft - this.renderedCards[0].offsetLeft), this.maximum));
    }, options);
    this.resizeObserver = new ResizeObserver(() => { this.update(); this.scheduleAutoplay(); });
    this.resizeObserver.observe(this.track);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.scheduleAutoplay();
    }, { threshold: 0.2 });
    this.intersectionObserver.observe(this);
    this.update();
  }

  syncLayout() {
    const loopActive = this.mobileMedia.matches && this.cards.length > 1;
    if (this.loopActive === loopActive) return false;
    this.track.querySelectorAll('[data-gallery-clone]').forEach((card) => card.remove());
    this.loopActive = loopActive;
    if (loopActive) {
      const cloneCard = (card, index) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('data-gallery-card');
        clone.removeAttribute('data-block-id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('data-gallery-clone', String(index));
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        // Side previews keep their product links, without duplicating keyboard stops.
        clone.querySelectorAll('a, button, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('img').forEach((image) => image.loading = 'eager');
        return clone;
      };
      this.track.prepend(cloneCard(this.cards.at(-1), this.cards.length - 1));
      this.track.append(cloneCard(this.cards[0], 0));
    }
    this.renderedCards = [...this.track.querySelectorAll('.furniture-gallery__card')];
    return true;
  }

  update() {
    const layoutChanged = this.syncLayout();
    this.maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const firstOffset = this.renderedCards[0]?.offsetLeft || 0;
    const cardPositions = this.renderedCards.map((card) => Math.min(Math.abs(card.offsetLeft - firstOffset), this.maximum));
    this.positions = this.loopActive ? cardPositions : [...new Set(cardPositions)];
    let position = Math.abs(this.track.scrollLeft);
    if (layoutChanged) {
      position = cardPositions[this.activeCardIndex + (this.loopActive ? 1 : 0)] || 0;
      this.scrollToPosition(position, true);
    }
    this.index = this.positions.reduce((nearest, target, index) =>
      Math.abs(target - position) < Math.abs(this.positions[nearest] - position) ? index : nearest, 0);
    const activeCard = this.renderedCards[this.index];
    this.activeCardIndex = activeCard?.hasAttribute('data-gallery-clone')
      ? Number(activeCard.dataset.galleryClone) : Math.max(0, this.cards.indexOf(activeCard));
    if (this.controls) this.controls.hidden = this.maximum < 2;
    if (this.previous) this.previous.disabled = !this.loopActive && position < 2;
    if (this.next) this.next.disabled = !this.loopActive && position >= this.maximum - 2;
    if (this.pause) this.pause.hidden = this.reducedMotion.matches;
    if (!this.counter) return;
    if (this.mobileMedia.matches) {
      this.counter.textContent = `${this.activeCardIndex + 1} / ${this.cards.length}`;
      return;
    }
    const trackRect = this.track.getBoundingClientRect();
    const visible = this.cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
      .filter(({ rect }) => rect.right > trackRect.left + 2 && rect.left < trackRect.right - 2);
    if (visible.length) this.counter.textContent = `${visible[0].index + 1}–${visible.at(-1).index + 1} / ${this.cards.length}`;
  }

  scrollToPosition(position, instant = false) {
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: position * direction, behavior: instant || this.reducedMotion.matches ? 'instant' : 'smooth' });
  }

  normalizeLoop() {
    if (!this.loopActive || this.dragging) return;
    this.update();
    const position = Math.abs(this.track.scrollLeft);
    if (Math.abs(position - this.positions[this.index]) > 2) return;
    if (this.index === 0 || this.index === this.renderedCards.length - 1) {
      const equivalentIndex = this.index === 0 ? this.renderedCards.length - 2 : 1;
      this.scrollToPosition(this.positions[equivalentIndex], true);
      this.update();
    }
  }

  move(step, loop = false) {
    this.update();
    this.normalizeLoop();
    const nextIndex = !this.loopActive && loop && this.index === this.positions.length - 1 ? 0 : Math.max(0, Math.min(this.index + step, this.positions.length - 1));
    this.scrollToPosition(this.positions[nextIndex] || 0);
    this.scheduleAutoplay();
  }

  stopAutoplay() { clearTimeout(this.timer); }

  scheduleAutoplay() {
    this.stopAutoplay();
    // Keyboard focus pauses motion; a touch or mouse tap must not leave it
    // paused indefinitely after using the controls or swiping a product.
    const keyboardFocus = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
    if (this.dataset.autoplay !== 'true' || this.manualPause || this.dragging || this.touching || !this.inView || document.hidden || this.reducedMotion.matches || keyboardFocus || this.maximum < 2) return;
    this.timer = setTimeout(() => this.move(1, true), Number(this.dataset.interval) || 3000);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this.settleTimer);
    cancelAnimationFrame(this.frame);
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.track?.querySelectorAll('[data-gallery-clone]').forEach((card) => card.remove());
    this.loopActive = undefined;
    this.controller = null;
  }
}

if (!customElements.get('furniture-gallery-slider')) {
  customElements.define('furniture-gallery-slider', FurnitureGallerySlider);
}
